import {
  fitImageInContainer,
  stampAreaToWorkspaceBounds,
  STAMP_DISPLAY_CONTAINER,
} from '@/image-designer/adapters/stampAreasAdapter';
import { computeImageLayout } from '@/image-designer/utils/stampPreviewSync';
import { getWorkspaceSizeFromFabricJson } from '@/image-designer/utils/slideModel';

/**
 * 解析 slide 对应的设计器工作区尺寸（用于 stampAreas 坐标反算）
 */
export function resolveWorkspaceSizeForSlide(
  templateImageId,
  templateData,
  fallbackWidth,
  fallbackHeight,
) {
  if (templateData) {
    const slideCanvas =
      templateData.slideCanvases?.[templateImageId]
      || (templateData.primaryImageId === templateImageId ? templateData.fabricCanvas : null);
    if (slideCanvas) {
      return getWorkspaceSizeFromFabricJson(slideCanvas);
    }
    const imgMeta = (templateData.images || []).find(
      (img) => String(img.id) === String(templateImageId),
    );
    if (imgMeta?.width && imgMeta?.height) {
      return { width: imgMeta.width, height: imgMeta.height };
    }
  }
  return {
    width: fallbackWidth || STAMP_DISPLAY_CONTAINER,
    height: fallbackHeight || STAMP_DISPLAY_CONTAINER,
  };
}

/**
 * @deprecated 仅用于兼容旧逻辑；合成应使用 mapStampAreaToTemplatePixels
 */
export function resolveStampDisplayScale(templateNaturalWidth, templateNaturalHeight) {
  const fit = fitImageInContainer(
    templateNaturalWidth,
    templateNaturalHeight,
    STAMP_DISPLAY_CONTAINER,
    STAMP_DISPLAY_CONTAINER,
  );
  return {
    scaleX: templateNaturalWidth / fit.imageDisplayWidth,
    scaleY: templateNaturalHeight / fit.imageDisplayHeight,
  };
}

/**
 * stampAreas（900 展示坐标）→ 模版原图像素
 * 先还原为工作区坐标，再按工作区/模版尺寸比映射，避免 letterbox 偏移被重复缩放。
 */
export function mapStampAreaToTemplatePixels(
  stamp,
  templateNaturalWidth,
  templateNaturalHeight,
  workspaceSize,
) {
  const wsW = workspaceSize?.width || templateNaturalWidth;
  const wsH = workspaceSize?.height || templateNaturalHeight;
  const bounds = stampAreaToWorkspaceBounds(stamp, { width: wsW, height: wsH });
  const scaleX = templateNaturalWidth / wsW;
  const scaleY = templateNaturalHeight / wsH;

  return {
    x: bounds.left * scaleX,
    y: bounds.top * scaleY,
    width: Math.max(1, bounds.width * scaleX),
    height: Math.max(1, bounds.height * scaleY),
    rotation: bounds.angle || Number(stamp.stampRotation) || 0,
  };
}

/**
 * 在 canvas 上绘制印花（与 IPE 设计器参考印花：fillMode / objectPosition / 区域旋转）
 */
export function drawStampImageInArea(ctx, stampImg, stamp, mapped) {
  const fillMode = stamp.fillMode || 'contain';
  const objectPosition = stamp.objectPosition || 'center center';

  const layout = computeImageLayout(
    stampImg.naturalWidth,
    stampImg.naturalHeight,
    mapped.width,
    mapped.height,
    fillMode,
    objectPosition,
  );

  ctx.save();

  const centerX = mapped.x + mapped.width / 2;
  const centerY = mapped.y + mapped.height / 2;
  if (mapped.rotation) {
    ctx.translate(centerX, centerY);
    ctx.rotate((mapped.rotation * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);
  }

  ctx.beginPath();
  ctx.rect(mapped.x, mapped.y, mapped.width, mapped.height);
  ctx.clip();

  ctx.drawImage(
    stampImg,
    mapped.x + layout.offsetX,
    mapped.y + layout.offsetY,
    layout.drawW,
    layout.drawH,
  );

  ctx.restore();
}
